import React, {useContext, useEffect, useState, useCallback, useMemo} from "react";
import BedRockContext from "../../BedRockContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {GenericTable} from "../generic/table/GenericTable";
import {defaultList} from "../generic/InitialStates";
import {HeaderColumnFormatter} from "../../column-formatters/HeaderColumnFormatter";
import {withSyncDot} from "../../column-formatters/SyncStatusColumnFormatter";
import {ENTITY_ID_QR_AUTO_PURCHASE_VEHICLE} from "../generic/Constants";
import {SyncProgressDialog} from "../../generic-components/SyncProgressDialog";
import {useSyncOperations} from "../../hooks/useSyncOperations";
import {useSyncEnabled} from "../../hooks/useSyncEnabled";
import {QR_PURCHASE_TYPE_LABELS} from "./qrReasonLabels";
import {QrCodeGeneratorDialog} from "./QrCodeGeneratorDialog";
import {QrAutoPurchaseHowItWorks} from "./QrAutoPurchaseHowItWorks";

// Product on the first line, each service (name × qty) on its own smaller line below
function formatProductAndServices(cell, row) {
    const services = Array.isArray(row.serviceItems) ? row.serviceItems : [];
    return (
        <div>
            <div className="fw-semibold">{row.inventoryItemName || "-"}</div>
            {services.map((s, i) => (
                <div key={i} className="text-muted fs-8">
                    + {s.inventoryItemName || "-"} × {s.quantity != null ? s.quantity : 1}
                </div>
            ))}
        </div>
    );
}

export function QrAutoPurchaseVehiclesPage() {
    const myData = useContext(BedRockContext);
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);

    // Read-only on the client: configured on the server and pulled here
    const {syncEnabled, pushEnabled, pullEnabled} = useSyncEnabled(ENTITY_ID_QR_AUTO_PURCHASE_VEHICLE);

    const [showQrGenerator, setShowQrGenerator] = useState(false);

    const reloadData = useCallback(() => {
        setReload(prev => prev + 1);
    }, []);

    const {
        showDialog: showSyncDialog,
        syncType,
        isInProgress,
        syncResult,
        pullFromServer,
        closeDialog: closeSyncDialog
    } = useSyncOperations(ENTITY_ID_QR_AUTO_PURCHASE_VEHICLE, reloadData, {pushEnabled: false, pullEnabled});

    const profileBreadCrumbs = [
        {title: "Home", path: "/dashboard", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
        {title: "Purchases", path: "/purchases-list", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
    ];

    const columns = useMemo(() => [
        {
            dataField: "vehicleNumber",
            text: "Vehicle",
            search: true,
            formatter: syncEnabled ? withSyncDot(HeaderColumnFormatter) : HeaderColumnFormatter
        },
        {
            dataField: "supplierName",
            text: "Supplier",
            search: true,
            formatter: (cell) => cell || "-"
        },
        {
            dataField: "inventoryItemName",
            text: "Product & Services",
            formatter: formatProductAndServices
        },
        {
            dataField: "purchaseType",
            text: "Type",
            formatter: (cell) => QR_PURCHASE_TYPE_LABELS[cell] || cell || "-"
        },
        {
            dataField: "weighbridgeName",
            text: "Weighbridge",
            formatter: (cell, row) => (row.weighbridgeId ? (cell || row.weighbridgeId) : "Any")
        },
        {
            dataField: "enabled",
            text: "Enabled",
            formatter: (cell) => (cell === false
                ? <span className="badge badge-light-danger">No</span>
                : <span className="badge badge-light-success">Yes</span>)
        },
    ], [syncEnabled]);

    useEffect(() => {
        if (myData && myData.setBaseData) {
            const toolBarButtons = [{
                name: "Generate QR Code",
                color: "primary",
                icon: "fa fa-qrcode",
                onClick: () => setShowQrGenerator(true),
            }];
            if (syncEnabled && pullEnabled) {
                toolBarButtons.push({
                    name: "Pull from Server",
                    color: "success",
                    icon: "fa fa-cloud-download-alt",
                    onClick: pullFromServer,
                });
            }
            myData.setBaseData({toolBarButtons});
        }
        setPageLoadComplete(true);
    }, [pullFromServer, syncEnabled, pullEnabled]);

    const BaseCard = () => (
        <div className="card card-body">
            {pageLoadComplete && (
                <>
                    <QrAutoPurchaseHowItWorks/>
                    <GenericTable
                        reload={reload}
                        defaultData={defaultList}
                        columns={columns}
                        entityId={ENTITY_ID_QR_AUTO_PURCHASE_VEHICLE}
                    />
                </>
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>QR Auto-Purchase Vehicles</PageTitle>
            <BaseCard/>
            <QrCodeGeneratorDialog show={showQrGenerator} handleClose={() => setShowQrGenerator(false)}/>
            {syncEnabled && (
                <SyncProgressDialog
                    show={showSyncDialog}
                    onClose={closeSyncDialog}
                    syncType={syncType}
                    isInProgress={isInProgress}
                    syncResult={syncResult}
                />
            )}
        </>
    );
}
